import { Button } from '@/components/ui/button';
import { Printer, ChefHat, Clock, DollarSign, Shield, Check } from 'lucide-react';

const SalesSummaryPDF = () => {
  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      {/* Print button - hidden when printing */}
      <div className="fixed top-4 right-4 print:hidden z-50">
        <Button onClick={handlePrint} size="lg" className="gap-2">
          <Printer className="h-5 w-5" />
          Save as PDF
        </Button>
      </div>

      {/* Main content - optimized for single page print */}
      <div className="min-h-screen bg-white text-gray-900 p-8 print:p-6 max-w-[8.5in] mx-auto">
        
        {/* Header */}
        <header className="flex items-center justify-between border-b-2 border-primary pb-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-primary rounded-lg p-2">
              <ChefHat className="h-8 w-8 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-primary">CampChef Pro</h1>
              <p className="text-sm text-gray-600">Camp Kitchen Management Software</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Prepared for</p>
            <p className="text-lg font-semibold">Camp Nor'wester</p>
            <p className="text-xs text-gray-400">{new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
          </div>
        </header>

        {/* Value Proposition - 3 Key Benefits */}
        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Why Camp Kitchens Choose CampChef Pro</h2>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="h-5 w-5 text-green-600" />
                <span className="font-bold text-green-800">Save 8+ Hours/Week</span>
              </div>
              <p className="text-sm text-green-700">Automated menu planning, inventory tracking, and order generation eliminate manual spreadsheet work.</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="h-5 w-5 text-blue-600" />
                <span className="font-bold text-blue-800">Reduce Costs 15-20%</span>
              </div>
              <p className="text-sm text-blue-700">AI-powered portion control, waste tracking, and smart reorder alerts optimize your food budget.</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-5 w-5 text-purple-600" />
                <span className="font-bold text-purple-800">Eliminate Allergen Risks</span>
              </div>
              <p className="text-sm text-purple-700">Automated allergen detection across all recipes and menus keeps campers safe.</p>
            </div>
          </div>
        </section>

        {/* Recommended Plan */}
        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Recommended Plan for Camp Nor'wester</h2>
          <div className="bg-primary/5 border-2 border-primary rounded-lg p-5">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="bg-primary text-primary-foreground text-xs font-semibold px-2 py-1 rounded">BEST FIT</span>
                  <h3 className="text-xl font-bold text-primary">Professional Plan</h3>
                </div>
                <p className="text-gray-600 mt-1">Ideal for growing camps managing multiple sessions and larger staff</p>
              </div>
              <div className="text-right">
                <p className="text-3xl font-bold text-primary">$279</p>
                <p className="text-sm text-gray-500">/month</p>
                <p className="text-xs text-green-600 font-medium">Save 17% annually</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-8 gap-y-2 mt-4">
              {[
                'Up to 500 active campers',
                'Advanced Inventory Analytics',
                'Auto Sysco Sync & Reorder Reports',
                'Team Collaboration Tools',
                'Custom Menu Templates',
                'Up to 10 Staff Accounts',
                'Priority email support',
                'Recipe & Menu Planning'
              ].map((feature) => (
                <div key={feature} className="flex items-center gap-2">
                  <Check className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-sm text-gray-700">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Pricing Comparison */}
        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">All Plans at a Glance</h2>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-gray-100">
                <th className="text-left p-2 border border-gray-200 font-semibold">Plan</th>
                <th className="text-left p-2 border border-gray-200 font-semibold">Price</th>
                <th className="text-left p-2 border border-gray-200 font-semibold">Best For</th>
                <th className="text-left p-2 border border-gray-200 font-semibold">Key Features</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-2 border border-gray-200 font-medium">Foundation</td>
                <td className="p-2 border border-gray-200">$179/mo</td>
                <td className="p-2 border border-gray-200">Small camps (up to 100 campers)</td>
                <td className="p-2 border border-gray-200">Basic inventory, CSV exports, 4 accounts</td>
              </tr>
              <tr className="bg-primary/5">
                <td className="p-2 border border-gray-200 font-medium text-primary">Professional ★</td>
                <td className="p-2 border border-gray-200 font-semibold text-primary">$279/mo</td>
                <td className="p-2 border border-gray-200">Growing camps (up to 500 campers)</td>
                <td className="p-2 border border-gray-200">Auto sync, analytics, 10 accounts</td>
              </tr>
              <tr>
                <td className="p-2 border border-gray-200 font-medium">Enterprise</td>
                <td className="p-2 border border-gray-200">Custom</td>
                <td className="p-2 border border-gray-200">Multi-site or year-round operations</td>
                <td className="p-2 border border-gray-200">Full EDI, API, dedicated support</td>
              </tr>
            </tbody>
          </table>
        </section>

        {/* Next Steps */}
        <section className="mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Next Steps</h2>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-2 font-bold">1</div>
                <p className="font-semibold text-gray-800">Schedule Demo</p>
                <p className="text-xs text-gray-600">15-minute personalized walkthrough</p>
              </div>
              <div className="text-center">
                <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-2 font-bold">2</div>
                <p className="font-semibold text-gray-800">Start Free Trial</p>
                <p className="text-xs text-gray-600">Import your actual recipes & menus</p>
              </div>
              <div className="text-center">
                <div className="bg-primary text-primary-foreground rounded-full w-8 h-8 flex items-center justify-center mx-auto mb-2 font-bold">3</div>
                <p className="font-semibold text-gray-800">Go Live</p>
                <p className="text-xs text-gray-600">Ready in as little as 1 week</p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-gray-200 pt-4 flex items-center justify-between text-sm text-gray-600">
          <div>
            <p className="font-medium text-gray-800">Questions? Let's talk.</p>
            <p>sales@campchefpro.com • campchefpro.com</p>
          </div>
          <div className="text-right">
            <p className="font-medium text-primary">Schedule a demo:</p>
            <p>calendly.com/campchefpro</p>
          </div>
        </footer>
      </div>

      {/* Print-specific styles */}
      <style>{`
        @media print {
          @page {
            size: letter;
            margin: 0.5in;
          }
          body {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>
    </>
  );
};

export default SalesSummaryPDF;
